import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../App'
import { api, Group, GroupMember, User } from '../services/api'

export default function GroupMembersPage() {
  const { groupId } = useParams<{ groupId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [group, setGroup] = useState<Group | null>(null)
  const [members, setMembers] = useState<GroupMember[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<User[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedAction, setSelectedAction] = useState<{ memberId: string; memberName: string } | null>(null)

  const isAdmin = group?.my_role === 'admin' || group?.my_role === 'owner'
  const isOwner = group?.my_role === 'owner'

  useEffect(() => {
    if (groupId) loadData()
  }, [groupId])

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }
    const timer = setTimeout(async () => {
      setIsSearching(true)
      try {
        const users = await api.searchUsers(searchQuery.trim())
        const memberIds = new Set(members.map(m => m.id))
        setSearchResults(users.filter(u => !memberIds.has(u.id)))
      } catch {
        console.error('Search failed')
      } finally {
        setIsSearching(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery, members])

  const loadData = async () => {
    try {
      setIsLoading(true)
      const [groupData, membersData] = await Promise.all([
        api.getGroup(groupId!),
        api.getGroupMembers(groupId!),
      ])
      setGroup(groupData)
      setMembers(membersData)
    } catch (error) {
      console.error('Failed to load:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddMember = async (userId: string) => {
    if (!groupId) return
    try {
      await api.addGroupMembers(groupId, [userId])
      setSearchQuery('')
      setSearchResults([])
      await loadData()
    } catch (error) {
      console.error('Failed to add member:', error)
      alert('添加成員失敗')
    }
  }

  const handleRemoveMember = async (memberId: string) => {
    if (!groupId) return
    if (!confirm('確定要移除此成員嗎？')) return
    try {
      await api.removeGroupMember(groupId, memberId)
      setMembers(prev => prev.filter(m => m.id !== memberId))
      setSelectedAction(null)
    } catch (error) {
      console.error('Failed to remove member:', error)
    }
  }

  const handleChangeRole = async (memberId: string, currentRole: string) => {
    if (!groupId) return
    const newRole = currentRole === 'admin' ? 'member' : 'admin'
    try {
      await api.updateMemberRole(groupId, memberId, newRole)
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, role: newRole } : m))
      setSelectedAction(null)
    } catch (error) {
      console.error('Failed to change role:', error)
      alert('更改角色失敗')
    }
  }

  const roleLabel = (role: string) => {
    switch (role) {
      case 'owner': return '擁有者'
      case 'admin': return '管理員'
      default: return '成員'
    }
  }

  const roleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-yellow-500/20 text-yellow-500'
      case 'admin': return 'bg-blue-500/20 text-blue-500'
      default: return 'bg-gray-500/20 text-gray-500'
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg">
        <div className="spinner border-primary border-t-primary-light" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <header className="app-bar">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-semibold flex-1">成員 ({members.length})</h1>
        {isAdmin && (
          <button onClick={() => setShowAddModal(true)} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </button>
        )}
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {members.map(member => (
            <button
              key={member.id}
              onClick={() => {
                if (isOwner && member.id !== user?.id && member.role !== 'owner') {
                  setSelectedAction({ memberId: member.id, memberName: member.username })
                } else if (member.id !== user?.id) {
                  navigate(`/profile/${member.id}`)
                }
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white dark:hover:bg-dark-surface transition-colors"
            >
              <div className="avatar avatar-sm">
                {member.avatar_url ? (
                  <img src={member.avatar_url} alt={member.username} className="w-full h-full object-cover rounded-full" />
                ) : (
                  member.username.charAt(0).toUpperCase()
                )}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="font-medium text-gray-900 dark:text-white truncate">
                  {member.username}
                  {member.id === user?.id && <span className="text-gray-400 text-sm ml-1">（你）</span>}
                </p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleBadgeColor(member.role)}`}>
                {roleLabel(member.role)}
              </span>
            </button>
          ))}
        </div>
      </main>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={() => setShowAddModal(false)}>
          <div className="bg-white dark:bg-dark-surface w-full max-w-lg rounded-t-2xl p-6 animate-slide-up max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">添加成員</h2>
            <div className="relative mb-4">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜尋用戶..."
                className="input-field pl-12"
                autoFocus
              />
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              {isSearching && (
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                  <div className="spinner w-4 h-4 border-primary border-t-primary-light" />
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {searchResults.map(u => (
                <button
                  key={u.id}
                  onClick={() => handleAddMember(u.id)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors rounded-xl"
                >
                  <div className="avatar avatar-sm">
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt={u.username} className="w-full h-full object-cover rounded-full" />
                    ) : (
                      u.username.charAt(0).toUpperCase()
                    )}
                  </div>
                  <span className="flex-1 text-left font-medium text-gray-900 dark:text-white">{u.username}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Member Action Sheet */}
      {selectedAction && (
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50" onClick={() => setSelectedAction(null)}>
          <div className="bg-white dark:bg-dark-surface w-full max-w-lg rounded-t-2xl overflow-hidden animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
              <p className="text-center font-semibold text-gray-900 dark:text-white">{selectedAction.memberName}</p>
            </div>
            <button
              onClick={() => {
                const member = members.find(m => m.id === selectedAction.memberId)
                if (member) handleChangeRole(member.id, member.role)
              }}
              className="w-full px-4 py-4 text-left text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-3"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              {members.find(m => m.id === selectedAction.memberId)?.role === 'admin'
                ? '移除管理員'
                : '設為管理員'}
            </button>
            <button
              onClick={() => handleRemoveMember(selectedAction.memberId)}
              className="w-full px-4 py-4 text-left text-red-500 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-3"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
              </svg>
              移除成員
            </button>
            <button
              onClick={() => setSelectedAction(null)}
              className="w-full px-4 py-4 text-center text-gray-500 font-medium border-t border-gray-100 dark:border-gray-800"
            >
              取消
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
